import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
} from "react";
import {
  userProfile,
  RawWorkExperience,
  education,
} from "../pages/profile/types";
import { toast } from "sonner";
import { API_URL } from "../utils/constants";

interface profileContextType {
  profileInfo: userProfile | null;
  handleFocus: (name: string, section: string, index?: number) => void;
  current: { fieldName: string; section: string; index?: number };
  updatedField: { fieldName: string; index: number };
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  setProfileInfo: React.Dispatch<React.SetStateAction<userProfile | null>>;
  getUserInfo: (value: any) => void;
  unSaved: boolean;
  setUnsaved: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>, name: string) => void;
  handleDiscardData: (name: string) => void;
  handleDiscard: (name: string) => void;
}

const ProfileContext = createContext<profileContextType | undefined>(undefined);

interface profileProp {
  children: ReactNode;
}

//update
const getRole = () => {
  const merchantRole = localStorage.getItem("Role");
  if (merchantRole !== null) {
    return merchantRole;
  } else return null;
};

export const useProfile = () => {
  const profile = useContext(ProfileContext);
  if (!profile) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return profile;
};

export const ProfileProvider: React.FC<profileProp> = ({ children }) => {
  const [profileInfo, setProfileInfo] = useState<userProfile | null>({
    aboutYou: "",
    aboutYouAsTeacher: "",
    firstName: "",
    lastName: "",
    otherNames: "",
    email: "",
    youtubeUrl: "",
    interests: [],
    languagesSpoken: [
      {
        language: "",
        proficiency: "",
      },
    ],
    workExperiences: [
      {
        organization: "",
        jobRole: "",
        startDate: "",
        endDate: "",
      },
    ],
    educationalQualifications: [
      {
        level: "",
        endDate: "",
        startDate: "",
        institution: "",
        fieldOfStudy: "",
      },
    ],
    dpUrl: "",
    otherCertifications: [],
  });

  const role = localStorage.getItem("Role");
  const { dpUrl, ...profileInfoWithDp } = profileInfo ?? ({} as userProfile);

  const getUserInfo = (value: any) => {
    setProfileInfo({
      ...profileInfoWithDp,
      dpUrl: role === "partner" ? value.profilePictureUrl : value.dpUrl,
      aboutYou: value.aboutYou,
      aboutYouAsTeacher: value.aboutYouAsTeacher,
      firstName: value.firstName,
      lastName: value.lastName,
      otherNames: value.otherName,
      email: value.email,
      youtubeUrl: value.youtubeVideoUrl,
      interests: value.interests,
      languagesSpoken: [
        {
          language: value.language,
          proficiency: value.proficiency,
        },
      ],
      workExperiences: value?.workExperiences?.map(
        (item: RawWorkExperience) => ({
          organization: item?.organization,
          jobRole: item?.jobRole,
          startDate: item?.startDate,
          endDate: item?.endDate,
        })
      ),
      educationalQualifications: value?.educationalQualifications?.map(
        (item: education) => ({
          level: item?.level,
          institution: item?.institution,
          fieldOfStudy: item?.fieldOfStudy,
          startDate: item?.startDate,
          endDate: item?.endDate,
        })
      ),
      // dpUrl: value?.dpUrl,
      otherCertifications: value?.otherCertifications,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (profileInfo) {
      setProfileInfo((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    }
  };
  const [unSaved, setUnsaved] = useState<boolean>(false);
  const [previousValue, setPreviousValue] = useState<
    string | RawWorkExperience | string[] | education
  >();
  const [current, setCurrent] = useState<{
    fieldName: string;
    section: string;
    index: number;
  }>({
    fieldName: "",
    section: "",
    index: 0,
  });

  const [updatedField, setUpdatedField] = useState<{
    fieldName: string;
    index: number;
  }>({
    fieldName: "",
    index: 0,
  });

  //   handleFocus
  const handleFocus = (name: string, section: string, index?: number) => {
    if (!previousValue) {
      let data = profileInfo?.[name as keyof typeof profileInfo];
      setPreviousValue(
        data as string | string[] | education | RawWorkExperience
      );
    }

    if (current?.fieldName === "" || current?.fieldName === name) {
      setCurrent({
        ...current,
        fieldName: name,
        section: section,
        // index: index ?? 0,
      });
    } else {
      setUnsaved(true);
      if (name && name !== current?.fieldName) {
        toast.warning("You need to save your previous change", {
          richColors: true,
        });
        setUpdatedField({ ...updatedField, fieldName: current?.fieldName });
      } else if (index) {
        toast.warning("You need to save your previous  change", {
          richColors: true,
        });
        setUpdatedField({
          ...updatedField,
          fieldName: current?.fieldName,
          // index: current?.index,
        });
      } else if (section) {
        toast.warning("You need to save your previous change", {
          richColors: true,
        });
        setUpdatedField({
          ...updatedField,
          fieldName: current?.fieldName,
          // index: current?.index,
        });
      }
    }
  };

  const handleSubmit = async (e: any, name: string) => {
    e.preventDefault();

    let changedData = profileInfo
      ? { [name]: profileInfo[name as keyof typeof profileInfo] }
      : null;

    if (changedData) {
      if (
        changedData[name] !== "" ||
        changedData[name] !== null ||
        changedData[name] !== "[]"
      ) {
        const token = localStorage.getItem("token");
        const id = localStorage.getItem("id");
        const userRole = getRole();
        let url;
        //  `;
        if (userRole === "teacher") {
          url = `${API_URL}/teachers/${id}`;
        } else {
          url = `${API_URL}/merchants/partners`;
        }

        toast.info("updating Profile", {
          richColors: true,
          duration: 3000,
        });
        try {
          const response = await fetch(url, {
            method: "PATCH",
            headers: {
              authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(changedData),
          });

          const result = await response.json();

          if (response?.ok) {
            toast.success(`${getRole()} updated successfully`, {
              richColors: true,
              duration: 3000,
            });

            setPreviousValue("");
            setCurrent({ ...current, fieldName: "", section: "", index: 0 });
            setUpdatedField({ ...current, fieldName: "", index: 0 });
          } else {
            throw new Error(`HTTP error!: ${response.status} please try again`);
          }
        } catch (error: any) {
          if (error.statusCode === 401) {
            window.location.href = "/login";
            toast.error(error.message || "Session Expired", {
              richColors: true,
              duration: 3000,
            });
          }
          toast.error(error.message || "Some went wrong. Please try again", {
            richColors: true,
            duration: 3000,
          });
        }
      }
    } else console.warn("an error occured", changedData);
  };

  const handleDiscard = (name: string) => {
    if (name === "interests") {
      setPreviousValue("");
      setCurrent({ ...current, fieldName: "", section: "", index: 0 });
      setUpdatedField({ ...current, fieldName: "", index: 0 });
    } else {
      setPreviousValue("");
      setCurrent({ ...current, fieldName: "", section: "", index: 0 });
      setUpdatedField({ ...current, fieldName: "", index: 0 });
      setProfileInfo((prev: any) => ({
        ...prev,
        [name]: previousValue,
      }));
    }
  };
  //for work experiences
  const handleDiscardData = (name: string) => {
    if (!previousValue) {
      let data = profileInfo?.[name as keyof typeof profileInfo];
      setPreviousValue(
        data as string | string[] | education | RawWorkExperience
      );
    }
  };
  return (
    <ProfileContext.Provider
      value={{
        handleDiscardData,
        handleDiscard,
        handleSubmit,
        unSaved,
        setUnsaved,
        profileInfo,
        handleFocus,
        current,
        updatedField,
        handleChange,
        setProfileInfo,
        getUserInfo,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};
